/**
 * Os participantes remotos no palco. UM só (a chamada 1:1 de sempre): o tile ocupa o palco inteiro, exatamente como antes.
 * DOIS ou mais (o plano do Otto permite várias pessoas por sala): uma grade que se adapta à quantidade, com páginas quando
 * não cabem todos. Só os tiles da página aparecem (uma sala de 100 pessoas não desenha 100 tiles); a ordem é estável
 * (quem entra vai para o fim) para ninguém mudar de lugar quando alguém fala ou liga a câmera. Quem não está na página
 * continua sendo ouvido (o áudio é da sala, não do tile) e aparece na aba "Participantes" do atendente.
 */
import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RemoteTile } from './ParticipantTile';
import { columnsFor, pageSizeFor, paginate, tileSizeFor } from './remoteGrid';
import { useMediaQuery } from './useMediaQuery';

/** Celular: o `sm` do Tailwind (a página do convidado troca de layout nesse ponto). */
const PHONE_QUERY = '(max-width: 639px)';

const PAGER_BUTTON = 'inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-40';

/**
 * @param {{
 *   tracks: Array<object>, variant: 'compact'|'room'|'guest', soloSize: 'lg'|'md'|'sm'|'xs', labelClassName?: string,
 * }} props `tracks`: as câmeras remotas, já em ordem estável (`orderRemoteTracks`). `soloSize` e `labelClassName` valem só
 *   para o tile único (1:1).
 */
export function RemoteGrid({ tracks, variant, soloSize, labelClassName }) {
    const phone = useMediaQuery(PHONE_QUERY);
    const [requestedPage, setPage] = useState(0);

    if (tracks.length === 1) return <RemoteTile trackRef={tracks[0]} size={soloSize} labelClassName={labelClassName} />;

    const compactLayout = variant === 'compact' || phone;
    const view = paginate(tracks, requestedPage, pageSizeFor(compactLayout));
    const columns = columnsFor(view.items.length, compactLayout);
    const rows = Math.ceil(view.items.length / columns);
    const size = tileSizeFor(view.items.length, compactLayout);

    return (
        <div
            data-remote-grid=""
            data-people={view.total}
            // No celular do convidado a barra flutua sobre o vídeo: a grade (e o paginador) termina acima dela
            className={cn('absolute inset-0 flex flex-col gap-2 p-2', variant === 'guest' && 'pb-[112px] sm:pb-2')}
        >
            <div
                role="group"
                aria-label="Participantes da chamada"
                className="grid min-h-0 flex-1 gap-2"
                style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}
            >
                {view.items.map((trackRef) => (
                    <div key={trackRef.participant.identity} data-grid-tile="" className="relative min-h-0 min-w-0 overflow-hidden rounded-xl">
                        <RemoteTile trackRef={trackRef} size={size} fit="cover" />
                    </div>
                ))}
            </div>
            {view.pages > 1 && (
                <nav aria-label="Páginas de participantes" className="flex shrink-0 items-center justify-center gap-2 text-xs text-white/80">
                    <button type="button" aria-label="Página anterior" disabled={view.page === 0} onClick={() => setPage(view.page - 1)} className={PAGER_BUTTON}>
                        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <span role="status" aria-live="polite" data-grid-pager="">
                        {`${view.total} pessoas, página ${view.page + 1} de ${view.pages}`}
                    </span>
                    <button type="button" aria-label="Próxima página" disabled={view.page >= view.pages - 1} onClick={() => setPage(view.page + 1)} className={PAGER_BUTTON}>
                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                </nav>
            )}
        </div>
    );
}
